import PageIcon from '@rsuite/icons/Page';
import { isExternalRouter } from 'auth/cognito';
import { showErrorNotification, showNotification } from 'components/ShowNotification';
import { SkillsMemberModel } from 'generated-types/skills/member';
import { ResumeProfileModel } from 'generated-types/skills/resume_profile';
import { useResumeProfilesHook } from 'hooks/skills/useResumeProfiles';
import { Dispatch, SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Checkbox, FlexboxGrid, IconButton, InputPicker, Panel, Tooltip, Whisper } from 'rsuite';
import HeaderPanelPreview from './HeaderPanelPreview';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import { AxiosError } from 'axios';

function getInputData(resumeProfiles: ResumeProfileModel[] | undefined) {
    if (resumeProfiles === undefined) return [];

    return resumeProfiles.map(profile => {
        return {
            label: profile.headline,
            value: profile.resume_profile_id,
        };
    });
}

export const HeaderPanel = ({
    previewMode,
    document,
    member,
    selectedMemberId,
    resumeProfiles,
    setSelectedResumeProfile,
    selectedResumeProfile,
    anonymous,
    setAnonymous,
}: {
    previewMode: boolean;
    document?: JSX.Element;
    member?: SkillsMemberModel;
    selectedMemberId?: number;
    resumeProfiles: ResumeProfileModel[] | undefined;
    setSelectedResumeProfile: Dispatch<SetStateAction<ResumeProfileModel | undefined>>;
    selectedResumeProfile: ResumeProfileModel;
    anonymous: boolean;
    setAnonymous: Dispatch<SetStateAction<boolean>>;
}) => {
    const useResumeProfiles = useResumeProfilesHook(
        member?.member_id,
        selectedResumeProfile.resume_profile_id,
        isExternalRouter,
    );

    const { createResumeProfile, refetchResumeProfiles, deleteResumeProfile } = useResumeProfiles;

    const navigate = useNavigate();

    return (
        <>
            <Panel style={{ marginBottom: '20px', overflow: 'visible' }}>
                <InputPicker
                    creatable
                    onSelect={value => {
                        const resume_profile_id = value;
                        const selectedResumeProfile = resumeProfiles?.find(
                            rp => rp.resume_profile_id === resume_profile_id,
                        );
                        if (selectedResumeProfile) {
                            setSelectedResumeProfile(selectedResumeProfile);
                        }
                    }}
                    onCreate={value => {
                        createResumeProfile.mutate(
                            {
                                headline: value,
                                about: '',
                                is_primary: false, /// Newly created profile is never primary
                            },
                            {
                                onSuccess: data => {
                                    if (data) {
                                        setSelectedResumeProfile({
                                            headline: data.headline,
                                            about: '',
                                            is_primary: data.is_primary,
                                            resume_profile_id: data.resume_profile_id,
                                            created_at: data.created_at,
                                            member_id: data.member_id,
                                            skill_ids: [],
                                        });
                                        showNotification({
                                            header: 'Resume profile created!',
                                        });
                                        refetchResumeProfiles();
                                    }
                                },
                                onError: error => {
                                    showErrorNotification(error);
                                },
                            },
                        );
                    }}
                    placeholder={selectedResumeProfile.headline}
                    groupBy="group"
                    data={getInputData(resumeProfiles)}
                    disabled={false}
                    size="lg"
                />
                <ConfirmationDialog
                    handleOK={() => {
                        deleteResumeProfile.mutate(undefined, {
                            onSuccess: () => {
                                // Handle success
                                showNotification({
                                    header: 'Resume profile deleted successfully',
                                });
                                refetchResumeProfiles();
                            },
                            onError: (error: AxiosError) => {
                                showErrorNotification(error);
                            },
                        });
                    }}
                    header={'Delete profile'}
                    message={'Are you sure you want to delete: ' + selectedResumeProfile.headline}
                >
                    <Whisper
                        trigger={resumeProfiles?.length === 1 ? 'hover' : 'none'}
                        placement="bottom"
                        speaker={<Tooltip>Cannot delete last profile</Tooltip>}
                    >
                        <Button
                            appearance="default"
                            style={{ marginLeft: '10px' }}
                            disabled={
                                deleteResumeProfile.isLoading ||
                                !selectedResumeProfile ||
                                resumeProfiles?.length === 1
                            }
                        >
                            Delete profile
                        </Button>
                    </Whisper>
                </ConfirmationDialog>
            </Panel>

            <Panel>
                <FlexboxGrid justify="space-between" style={{ alignItems: 'center' }}>
                    <FlexboxGrid.Item>
                        {selectedMemberId ? <h2>Resume</h2> : <h2>My Resume</h2>}
                    </FlexboxGrid.Item>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Checkbox
                            checked={anonymous}
                            onChange={(_, checked) => setAnonymous(checked)}
                        >
                            Make anonymous
                        </Checkbox>
                        {previewMode ? (
                            <HeaderPanelPreview
                                document={document}
                                member={member}
                                navigate={navigate}
                                selectedMemberId={selectedMemberId}
                                anonymous={anonymous}
                            />
                        ) : (
                            <IconButton
                                icon={<PageIcon />}
                                onClick={() => {
                                    navigate(
                                        selectedMemberId
                                            ? `/resume/${selectedMemberId}/preview`
                                            : '/resume/preview',
                                    );
                                }}
                            >
                                Show as PDF
                            </IconButton>
                        )}
                    </div>
                </FlexboxGrid>
            </Panel>
        </>
    );
};
